import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { adminCustomerPlanLabelKey, adminCustomerPlanOptions, adminCustomerSalesStatuses } from '@config/adminCustomerOptions';
import { AdminApiError } from '@services/admin/adminAuth';
import { adminCustomers } from '@services/admin/adminCustomers';
const pageSize = 10;
const emptyInput = {
    fullName: '',
    email: '',
    primaryPhone: '',
    secondaryPhone: '',
    address: '',
    planCode: 'none',
    customPlanName: '',
    salesStatus: 'new',
    enrollmentEndDate: '',
    lastContactDate: '',
    details: ''
};
export const AdminCustomersPanel = ({ role }) => {
    const { t, i18n } = useTranslation();
    const [query, setQuery] = useState({
        page: 1,
        pageSize,
        q: '',
        recordStatus: 'active',
        salesStatus: 'all',
        plan: '',
        sort: 'updatedAt',
        direction: 'desc'
    });
    const [searchInput, setSearchInput] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const [editor, setEditor] = useState(null);
    const [detail, setDetail] = useState(null);
    const [confirmation, setConfirmation] = useState(null);
    const [pendingId, setPendingId] = useState(null);
    const [success, setSuccess] = useState('');
    const loadErrorRef = useRef(null);
    const successRef = useRef(null);
    const lastTriggerRef = useRef(null);
    const canEdit = role === 'owner' || role === 'admin' || role === 'operator';
    const canArchive = role === 'owner' || role === 'admin';
    useEffect(() => {
        let current = true;
        setLoading(true);
        setLoadError(false);
        void adminCustomers
            .list(query)
            .then((response) => {
            if (!current)
                return;
            setResult(response);
        })
            .catch(() => {
            if (!current)
                return;
            setLoadError(true);
        })
            .finally(() => {
            if (current)
                setLoading(false);
        });
        return () => {
            current = false;
        };
    }, [query, reloadToken]);
    useEffect(() => {
        if (loadError)
            loadErrorRef.current?.focus();
    }, [loadError]);
    useEffect(() => {
        if (!success)
            return;
        successRef.current?.focus();
        const timeout = window.setTimeout(() => setSuccess(''), 4500);
        return () => window.clearTimeout(timeout);
    }, [success]);
    const totalPages = Math.max(1, Math.ceil((result?.total ?? 0) / pageSize));
    const refresh = useCallback(() => setReloadToken((value) => value + 1), []);
    const rememberTrigger = () => {
        lastTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    };
    const closeModal = () => {
        setEditor(null);
        setDetail(null);
        setConfirmation(null);
        window.setTimeout(() => lastTriggerRef.current?.focus(), 0);
    };
    const submitSearch = (event) => {
        event.preventDefault();
        setQuery((current) => ({ ...current, page: 1, q: searchInput.trim() }));
    };
    const openCreate = () => {
        rememberTrigger();
        setEditor({ mode: 'create', customer: null });
    };
    const loadCustomer = async (id, purpose) => {
        rememberTrigger();
        setPendingId(id);
        try {
            const response = await adminCustomers.detail(id);
            if (purpose === 'detail')
                setDetail(response.customer);
            else
                setEditor({ mode: 'edit', customer: response.customer });
        }
        catch {
            setLoadError(true);
        }
        finally {
            setPendingId(null);
        }
    };
    const requestStatusChange = (customer, action) => {
        rememberTrigger();
        setConfirmation({
            id: customer.id,
            fullName: customer.fullName,
            version: customer.version,
            action
        });
    };
    const changeStatus = async () => {
        if (!confirmation || pendingId)
            return;
        setPendingId(confirmation.id);
        try {
            if (confirmation.action === 'archive') {
                await adminCustomers.archive(confirmation.id, confirmation.version);
            }
            else {
                await adminCustomers.restore(confirmation.id, confirmation.version);
            }
            const successKey = confirmation.action === 'archive'
                ? 'admin.customers.messages.archived'
                : 'admin.customers.messages.restored';
            setSuccess(t(successKey));
            setConfirmation(null);
            if ((result?.items.length ?? 0) === 1 && query.page > 1) {
                setQuery((current) => ({ ...current, page: current.page - 1 }));
            }
            else {
                refresh();
            }
        }
        catch (failure) {
            setConfirmation(null);
            setSuccess('');
            if (failure instanceof AdminApiError && failure.code === 'customer_version_conflict') {
                setLoadError(true);
            }
            else {
                setLoadError(true);
            }
        }
        finally {
            setPendingId(null);
            window.setTimeout(() => lastTriggerRef.current?.focus(), 0);
        }
    };
    const handleSaved = (message) => {
        setSuccess(message);
        closeModal();
        refresh();
    };
    const sortValue = `${query.sort}-${query.direction}`;
    const planLabel = (customer) => customer.planCode === 'custom'
        ? customer.customPlanName || t('admin.customers.planOptions.custom')
        : t(adminCustomerPlanLabelKey(customer.planCode) ?? 'admin.customers.planOptions.none');
    return (_jsxs(Panel, { "aria-labelledby": "admin-customers-title", children: [_jsxs(PanelHeader, { children: [_jsxs("div", { children: [_jsx(Eyebrow, { children: t('admin.customers.eyebrow') }), _jsx(Title, { id: "admin-customers-title", children: t('admin.customers.title') }), _jsx(Count, { "aria-live": "polite", children: t('admin.customers.count', { count: result?.total ?? 0 }) })] }), canEdit && (_jsx(PrimaryButton, { type: "button", onClick: openCreate, children: t('admin.customers.new') }))] }), success && (_jsx(SuccessMessage, { ref: successRef, role: "status", tabIndex: -1, children: success })), _jsxs(Filters, { "aria-label": t('admin.customers.filters.label'), children: [_jsxs(SearchForm, { role: "search", onSubmit: submitSearch, children: [_jsx(Label, { htmlFor: "admin-customer-search", children: t('admin.customers.filters.searchLabel') }), _jsxs(SearchRow, { children: [_jsx(Input, { id: "admin-customer-search", type: "search", maxLength: 120, value: searchInput, placeholder: t('admin.customers.filters.searchPlaceholder'), onChange: (event) => setSearchInput(event.target.value) }), _jsx(SecondaryButton, { type: "submit", children: t('admin.customers.filters.search') })] })] }), _jsxs(FilterField, { children: [_jsx(Label, { htmlFor: "admin-customer-sales-status", children: t('admin.customers.filters.salesStatus') }), _jsxs(Select, { id: "admin-customer-sales-status", value: query.salesStatus, onChange: (event) => setQuery((current) => ({
                                    ...current,
                                    page: 1,
                                    salesStatus: event.target.value
                                })), children: [_jsx("option", { value: "all", children: t('admin.customers.salesStatus.all') }), adminCustomerSalesStatuses.map((status) => (_jsx("option", { value: status, children: t(`admin.customers.salesStatus.${status}`) }, status)))] })] }), _jsxs(FilterField, { children: [_jsx(Label, { htmlFor: "admin-customer-plan", children: t('admin.customers.filters.plan') }), _jsxs(Select, { id: "admin-customer-plan", value: query.plan, onChange: (event) => setQuery((current) => ({ ...current, page: 1, plan: event.target.value })), children: [_jsx("option", { value: "", children: t('admin.customers.filters.allPlans') }), adminCustomerPlanOptions.map((plan) => (_jsx("option", { value: plan.code, children: t(plan.labelKey) }, plan.code)))] })] }), _jsxs(FilterField, { children: [_jsx(Label, { htmlFor: "admin-customer-record-status", children: t('admin.customers.filters.recordStatus') }), _jsxs(Select, { id: "admin-customer-record-status", value: query.recordStatus, onChange: (event) => setQuery((current) => ({
                                    ...current,
                                    page: 1,
                                    recordStatus: event.target.value
                                })), children: [_jsx("option", { value: "active", children: t('admin.customers.recordStatus.activePlural') }), _jsx("option", { value: "archived", children: t('admin.customers.recordStatus.archivedPlural') }), _jsx("option", { value: "all", children: t('admin.customers.recordStatus.all') })] })] }), _jsxs(FilterField, { children: [_jsx(Label, { htmlFor: "admin-customer-sort", children: t('admin.customers.filters.sort') }), _jsxs(Select, { id: "admin-customer-sort", value: sortValue, onChange: (event) => {
                                    const [sort, direction] = event.target.value.split('-');
                                    setQuery((current) => ({ ...current, page: 1, sort, direction }));
                                }, children: [_jsx("option", { value: "updatedAt-desc", children: t('admin.customers.sort.updatedNewest') }), _jsx("option", { value: "updatedAt-asc", children: t('admin.customers.sort.updatedOldest') }), _jsx("option", { value: "name-asc", children: t('admin.customers.sort.nameAsc') }), _jsx("option", { value: "name-desc", children: t('admin.customers.sort.nameDesc') }), _jsx("option", { value: "email-asc", children: t('admin.customers.sort.emailAsc') }), _jsx("option", { value: "salesStatus-asc", children: t('admin.customers.sort.salesStatus') }), _jsx("option", { value: "plan-asc", children: t('admin.customers.sort.plan') }), _jsx("option", { value: "enrollmentEndDate-asc", children: t('admin.customers.sort.enrollmentEndDate') }), _jsx("option", { value: "lastContactDate-desc", children: t('admin.customers.sort.lastContactDate') })] })] })] }), loadError && (_jsxs(ErrorMessage, { ref: loadErrorRef, role: "alert", tabIndex: -1, children: [_jsx("span", { children: t('admin.customers.errors.load') }), _jsx(InlineButton, { type: "button", onClick: refresh, children: t('admin.customers.actions.retry') })] })), loading && !result ? (_jsx(StateBox, { "aria-live": "polite", children: t('admin.customers.loading') })) : !loadError && result?.items.length === 0 ? (_jsxs(StateBox, { children: [_jsx("strong", { children: t('admin.customers.empty.title') }), _jsx("span", { children: t('admin.customers.empty.body') })] })) : (result && (_jsxs(_Fragment, { children: [_jsx(DesktopTableRegion, { tabIndex: 0, role: "region", "aria-label": t('admin.customers.table.regionLabel'), "aria-busy": loading, children: _jsxs(CustomerTable, { children: [_jsx("caption", { children: t('admin.customers.table.caption') }), _jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { scope: "col", children: t('admin.customers.fields.fullName') }), _jsx("th", { scope: "col", children: t('admin.customers.fields.email') }), _jsx("th", { scope: "col", children: t('admin.customers.fields.primaryPhone') }), _jsx("th", { scope: "col", children: t('admin.customers.fields.salesStatus') }), _jsx("th", { scope: "col", children: t('admin.customers.fields.plan') }), _jsx("th", { scope: "col", children: t('admin.customers.fields.enrollmentEndDate') }), _jsx("th", { scope: "col", children: t('admin.customers.fields.lastContactDate') }), _jsx("th", { scope: "col", children: t('admin.customers.fields.details') }), _jsx("th", { scope: "col", children: t('admin.customers.table.actions') })] }) }), _jsx("tbody", { children: result.items.map((customer) => (_jsxs("tr", { children: [_jsxs(NameCell, { children: [customer.fullName, customer.recordStatus === 'archived' && (_jsx(RecordNote, { children: t('admin.customers.recordStatus.archived') }))] }), _jsx("td", { children: customer.email }), _jsxs("td", { children: [customer.primaryPhone || t('admin.customers.values.notProvided'), customer.hasSecondaryPhone && (_jsx(SecondaryPhoneIndicator, { title: t('admin.customers.values.hasSecondaryPhone'), "aria-label": t('admin.customers.values.hasSecondaryPhone'), children: "+1" }))] }), _jsx("td", { children: _jsxs(SalesStatusBadge, { "$status": customer.salesStatus, children: [_jsx(StatusMarker, { "aria-hidden": "true", children: salesStatusMarker(customer.salesStatus) }), t(`admin.customers.salesStatus.${customer.salesStatus}`)] }) }), _jsx("td", { children: planLabel(customer) }), _jsx("td", { children: formatCalendarDate(customer.enrollmentEndDate, i18n.language) }), _jsx("td", { children: formatCalendarDate(customer.lastContactDate, i18n.language) }), _jsx("td", { children: _jsx(DetailsPreview, { children: customer.detailsPreview || t('admin.customers.values.noDetails') }) }), _jsx("td", { children: _jsxs(RowActions, { children: [_jsx(ActionButton, { type: "button", onClick: () => void loadCustomer(customer.id, 'detail'), disabled: pendingId === customer.id, children: t('admin.customers.actions.view') }), canEdit && (_jsx(ActionButton, { type: "button", onClick: () => void loadCustomer(customer.id, 'edit'), disabled: pendingId === customer.id, children: t('admin.customers.actions.edit') })), canArchive && customer.recordStatus === 'active' && (_jsx(DangerButton, { type: "button", onClick: () => requestStatusChange(customer, 'archive'), children: t('admin.customers.actions.archive') })), canArchive && customer.recordStatus === 'archived' && (_jsx(ActionButton, { type: "button", onClick: () => requestStatusChange(customer, 'restore'), children: t('admin.customers.actions.restore') }))] }) })] }, customer.id))) })] }) }), _jsx(MobileList, { "aria-label": t('admin.customers.table.regionLabel'), "aria-busy": loading, children: result.items.map((customer) => (_jsxs(CustomerCard, { children: [_jsxs(CardTop, { children: [_jsxs("div", { children: [_jsx(CardName, { children: customer.fullName }), _jsx(CardEmail, { children: customer.email })] }), _jsxs(SalesStatusBadge, { "$status": customer.salesStatus, children: [_jsx(StatusMarker, { "aria-hidden": "true", children: salesStatusMarker(customer.salesStatus) }), t(`admin.customers.salesStatus.${customer.salesStatus}`)] })] }), _jsxs(CardGrid, { children: [_jsx("dt", { children: t('admin.customers.fields.primaryPhone') }), _jsxs("dd", { children: [customer.primaryPhone || t('admin.customers.values.notProvided'), customer.hasSecondaryPhone && (_jsx(SecondaryPhoneIndicator, { title: t('admin.customers.values.hasSecondaryPhone'), "aria-label": t('admin.customers.values.hasSecondaryPhone'), children: "+1" }))] }), _jsx("dt", { children: t('admin.customers.fields.plan') }), _jsx("dd", { children: planLabel(customer) }), _jsx("dt", { children: t('admin.customers.fields.enrollmentEndDate') }), _jsx("dd", { children: formatCalendarDate(customer.enrollmentEndDate, i18n.language) }), _jsx("dt", { children: t('admin.customers.fields.lastContactDate') }), _jsx("dd", { children: formatCalendarDate(customer.lastContactDate, i18n.language) }), _jsx("dt", { children: t('admin.customers.fields.details') }), _jsx("dd", { children: _jsx(DetailsPreview, { children: customer.detailsPreview || t('admin.customers.values.noDetails') }) }), customer.recordStatus === 'archived' && (_jsxs(_Fragment, { children: [_jsx("dt", { children: t('admin.customers.fields.recordStatus') }), _jsx("dd", { children: t('admin.customers.recordStatus.archived') })] }))] }), _jsxs(RowActions, { children: [_jsx(ActionButton, { type: "button", onClick: () => void loadCustomer(customer.id, 'detail'), disabled: pendingId === customer.id, children: t('admin.customers.actions.view') }), canEdit && (_jsx(ActionButton, { type: "button", onClick: () => void loadCustomer(customer.id, 'edit'), disabled: pendingId === customer.id, children: t('admin.customers.actions.edit') })), canArchive && (customer.recordStatus === 'active' ? (_jsx(DangerButton, { type: "button", onClick: () => requestStatusChange(customer, 'archive'), children: t('admin.customers.actions.archive') })) : (_jsx(ActionButton, { type: "button", onClick: () => requestStatusChange(customer, 'restore'), children: t('admin.customers.actions.restore') })))] })] }, customer.id))) }), _jsxs(Pagination, { "aria-label": t('admin.customers.pagination.label'), children: [_jsx(SecondaryButton, { type: "button", disabled: query.page <= 1 || loading, onClick: () => setQuery((current) => ({ ...current, page: current.page - 1 })), children: t('admin.customers.pagination.previous') }), _jsx("span", { "aria-live": "polite", children: t('admin.customers.pagination.page', { page: query.page, totalPages }) }), _jsx(SecondaryButton, { type: "button", disabled: query.page >= totalPages || loading, onClick: () => setQuery((current) => ({ ...current, page: current.page + 1 })), children: t('admin.customers.pagination.next') })] })] }))), editor && (_jsx(CustomerEditorDialog, { state: editor, onClose: closeModal, onSaved: handleSaved })), detail && (_jsx(CustomerDetailDialog, { customer: detail, locale: i18n.language, canEdit: canEdit, onEdit: () => {
                    setDetail(null);
                    setEditor({ mode: 'edit', customer: detail });
                }, onClose: closeModal })), confirmation && (_jsx(ConfirmationDialog, { state: confirmation, busy: pendingId === confirmation.id, onConfirm: () => void changeStatus(), onClose: closeModal }))] }));
};
const CustomerEditorDialog = ({ state, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [input, setInput] = useState(() => state.customer
        ? {
            fullName: state.customer.fullName,
            email: state.customer.email,
            primaryPhone: state.customer.primaryPhone ?? '',
            secondaryPhone: state.customer.secondaryPhone ?? '',
            address: state.customer.address ?? '',
            planCode: state.customer.planCode,
            customPlanName: state.customer.customPlanName ?? '',
            salesStatus: state.customer.salesStatus,
            enrollmentEndDate: state.customer.enrollmentEndDate ?? '',
            lastContactDate: state.customer.lastContactDate ?? '',
            details: state.customer.details ?? ''
        }
        : emptyInput);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const nameRef = useRef(null);
    const emailRef = useRef(null);
    const primaryPhoneRef = useRef(null);
    const secondaryPhoneRef = useRef(null);
    const addressRef = useRef(null);
    const planRef = useRef(null);
    const customPlanRef = useRef(null);
    const salesStatusRef = useRef(null);
    const enrollmentEndDateRef = useRef(null);
    const lastContactDateRef = useRef(null);
    const detailsRef = useRef(null);
    const formErrorRef = useRef(null);
    const refs = {
        fullName: nameRef,
        email: emailRef,
        primaryPhone: primaryPhoneRef,
        secondaryPhone: secondaryPhoneRef,
        address: addressRef,
        planCode: planRef,
        customPlanName: customPlanRef,
        salesStatus: salesStatusRef,
        enrollmentEndDate: enrollmentEndDateRef,
        lastContactDate: lastContactDateRef,
        details: detailsRef
    };
    const setField = (field, value) => {
        setInput((current) => ({ ...current, [field]: value }));
        if (errors[field])
            setErrors((current) => ({ ...current, [field]: undefined }));
    };
    const validate = () => {
        const next = {};
        if (!input.fullName.trim() || input.fullName.trim().length > 120) {
            next.fullName = t('admin.customers.validation.fullName');
        }
        if (!input.email.trim() ||
            input.email.length > 254 ||
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email.trim())) {
            next.email = t('admin.customers.validation.email');
        }
        const primaryPhoneNormalized = normalizedPhoneInput(input.primaryPhone);
        const secondaryPhoneNormalized = normalizedPhoneInput(input.secondaryPhone);
        if (input.primaryPhone && primaryPhoneNormalized === null) {
            next.primaryPhone = t('admin.customers.validation.phone');
        }
        if (input.secondaryPhone && secondaryPhoneNormalized === null) {
            next.secondaryPhone = t('admin.customers.validation.phone');
        }
        if (primaryPhoneNormalized &&
            secondaryPhoneNormalized &&
            primaryPhoneNormalized === secondaryPhoneNormalized) {
            next.secondaryPhone = t('admin.customers.validation.duplicatePhone');
        }
        if (input.address.length > 500)
            next.address = t('admin.customers.validation.address');
        if (!adminCustomerPlanOptions.some((option) => option.code === input.planCode)) {
            next.planCode = t('admin.customers.validation.plan');
        }
        if (input.planCode === 'custom') {
            if (!input.customPlanName.trim() || input.customPlanName.trim().length > 120) {
                next.customPlanName = t('admin.customers.validation.customPlanName');
            }
        }
        if (!adminCustomerSalesStatuses.includes(input.salesStatus)) {
            next.salesStatus = t('admin.customers.validation.salesStatus');
        }
        if (!validCalendarInput(input.enrollmentEndDate)) {
            next.enrollmentEndDate = t('admin.customers.validation.date');
        }
        if (!validCalendarInput(input.lastContactDate)) {
            next.lastContactDate = t('admin.customers.validation.date');
        }
        if (input.details.length > 5000)
            next.details = t('admin.customers.validation.details');
        return next;
    };
    const submit = async (event) => {
        event.preventDefault();
        if (busy)
            return;
        const nextErrors = validate();
        setErrors(nextErrors);
        const firstError = [
            'fullName',
            'email',
            'primaryPhone',
            'secondaryPhone',
            'address',
            'salesStatus',
            'planCode',
            'customPlanName',
            'enrollmentEndDate',
            'lastContactDate',
            'details'
        ].find((key) => Boolean(nextErrors[key]));
        if (firstError) {
            refs[firstError].current?.focus();
            return;
        }
        setBusy(true);
        try {
            if (state.mode === 'create') {
                await adminCustomers.create(input);
                onSaved(t('admin.customers.messages.created'));
            }
            else if (state.customer) {
                await adminCustomers.update(state.customer.id, state.customer.version, input);
                onSaved(t('admin.customers.messages.updated'));
            }
        }
        catch (failure) {
            if (failure instanceof AdminApiError && failure.code === 'customer_conflict') {
                setErrors({ email: t('admin.customers.validation.duplicateEmail') });
                emailRef.current?.focus();
            }
            else if (failure instanceof AdminApiError &&
                failure.code === 'customer_phone_conflict') {
                setErrors({
                    primaryPhone: t('admin.customers.validation.duplicatePhone'),
                    secondaryPhone: t('admin.customers.validation.duplicatePhone')
                });
                primaryPhoneRef.current?.focus();
            }
            else if (failure instanceof AdminApiError &&
                failure.code === 'customer_version_conflict') {
                setErrors({ form: t('admin.customers.errors.versionConflict') });
                formErrorRef.current?.focus();
            }
            else if (failure instanceof AdminApiError && failure.code === 'invalid_customer') {
                setErrors({ form: t('admin.customers.errors.invalid') });
                formErrorRef.current?.focus();
            }
            else {
                setErrors({ form: t('admin.customers.errors.save') });
                formErrorRef.current?.focus();
            }
        }
        finally {
            setBusy(false);
        }
    };
    return (_jsxs(Modal, { titleId: "admin-customer-editor-title", onClose: onClose, children: [_jsxs(DialogHeader, { children: [_jsxs("div", { children: [_jsx(Eyebrow, { children: t('admin.customers.form.eyebrow') }), _jsx(DialogTitle, { id: "admin-customer-editor-title", children: t(state.mode === 'create'
                                    ? 'admin.customers.form.createTitle'
                                    : 'admin.customers.form.editTitle') })] }), _jsx(CloseButton, { type: "button", onClick: onClose, "aria-label": t('admin.customers.actions.close'), children: "\u00D7" })] }), errors.form && (_jsx(ErrorMessage, { ref: formErrorRef, role: "alert", tabIndex: -1, children: errors.form })), _jsxs(EditorForm, { onSubmit: submit, noValidate: true, children: [_jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-customer-name", children: t('admin.customers.fields.fullName') }), _jsx(Input, { ref: nameRef, "data-autofocus": true, id: "admin-customer-name", required: true, maxLength: 120, value: input.fullName, "aria-invalid": Boolean(errors.fullName), "aria-describedby": errors.fullName ? 'admin-customer-name-error' : undefined, onChange: (event) => setField('fullName', event.target.value), disabled: busy }), errors.fullName && _jsx(FieldError, { id: "admin-customer-name-error", children: errors.fullName })] }), _jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-customer-email", children: t('admin.customers.fields.email') }), _jsx(Input, { ref: emailRef, id: "admin-customer-email", type: "email", inputMode: "email", autoComplete: "off", required: true, maxLength: 254, value: input.email, "aria-invalid": Boolean(errors.email), "aria-describedby": errors.email ? 'admin-customer-email-error' : undefined, onChange: (event) => setField('email', event.target.value), disabled: busy }), errors.email && _jsx(FieldError, { id: "admin-customer-email-error", children: errors.email })] }), _jsxs(TwoColumns, { children: [_jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-customer-primary-phone", children: t('admin.customers.fields.primaryPhone') }), _jsx(Input, { ref: primaryPhoneRef, id: "admin-customer-primary-phone", type: "tel", inputMode: "tel", autoComplete: "off", maxLength: 40, value: input.primaryPhone, "aria-invalid": Boolean(errors.primaryPhone), "aria-describedby": errors.primaryPhone ? 'admin-customer-primary-phone-error' : undefined, onChange: (event) => setField('primaryPhone', event.target.value), disabled: busy }), errors.primaryPhone && (_jsx(FieldError, { id: "admin-customer-primary-phone-error", children: errors.primaryPhone }))] }), _jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-customer-secondary-phone", children: t('admin.customers.fields.secondaryPhone') }), _jsx(Input, { ref: secondaryPhoneRef, id: "admin-customer-secondary-phone", type: "tel", inputMode: "tel", autoComplete: "off", maxLength: 40, value: input.secondaryPhone, "aria-invalid": Boolean(errors.secondaryPhone), "aria-describedby": errors.secondaryPhone ? 'admin-customer-secondary-phone-error' : undefined, onChange: (event) => setField('secondaryPhone', event.target.value), disabled: busy }), errors.secondaryPhone && (_jsx(FieldError, { id: "admin-customer-secondary-phone-error", children: errors.secondaryPhone }))] })] }), _jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-customer-address", children: t('admin.customers.fields.address') }), _jsx(Textarea, { ref: addressRef, id: "admin-customer-address", rows: 3, maxLength: 500, value: input.address, "aria-invalid": Boolean(errors.address), "aria-describedby": "admin-customer-address-count", onChange: (event) => setField('address', event.target.value), disabled: busy }), _jsx(CharacterCount, { id: "admin-customer-address-count", children: t('admin.customers.form.characterCount', { count: input.address.length, maximum: 500 }) }), errors.address && _jsx(FieldError, { children: errors.address })] }), _jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-customer-editor-sales-status", children: t('admin.customers.fields.salesStatus') }), _jsx(Select, { ref: salesStatusRef, id: "admin-customer-editor-sales-status", value: input.salesStatus, "aria-invalid": Boolean(errors.salesStatus), "aria-describedby": errors.salesStatus ? 'admin-customer-sales-status-error' : undefined, onChange: (event) => setField('salesStatus', event.target.value), disabled: busy, children: adminCustomerSalesStatuses.map((status) => (_jsx("option", { value: status, children: t(`admin.customers.salesStatus.${status}`) }, status))) }), errors.salesStatus && (_jsx(FieldError, { id: "admin-customer-sales-status-error", children: errors.salesStatus }))] }), _jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-customer-editor-plan", children: t('admin.customers.fields.plan') }), _jsx(Select, { ref: planRef, id: "admin-customer-editor-plan", value: input.planCode, "aria-invalid": Boolean(errors.planCode), "aria-describedby": errors.planCode ? 'admin-customer-plan-error' : undefined, onChange: (event) => {
                                    const planCode = event.target.value;
                                    setInput((current) => ({
                                        ...current,
                                        planCode,
                                        customPlanName: planCode === 'custom' ? current.customPlanName : ''
                                    }));
                                    setErrors((current) => ({
                                        ...current,
                                        planCode: undefined,
                                        ...(planCode === 'custom' ? {} : { customPlanName: undefined })
                                    }));
                                }, disabled: busy, children: adminCustomerPlanOptions.map((option) => (_jsx("option", { value: option.code, children: t(option.labelKey) }, option.code))) }), errors.planCode && (_jsx(FieldError, { id: "admin-customer-plan-error", children: errors.planCode }))] }), input.planCode === 'custom' && (_jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-customer-custom-plan", children: t('admin.customers.fields.customPlanName') }), _jsx(Input, { ref: customPlanRef, id: "admin-customer-custom-plan", required: true, maxLength: 120, value: input.customPlanName, "aria-invalid": Boolean(errors.customPlanName), "aria-describedby": errors.customPlanName ? 'admin-customer-custom-plan-error' : undefined, onChange: (event) => setField('customPlanName', event.target.value), disabled: busy }), errors.customPlanName && (_jsx(FieldError, { id: "admin-customer-custom-plan-error", children: errors.customPlanName }))] })), _jsxs(TwoColumns, { children: [_jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-customer-enrollment-end", children: t('admin.customers.fields.enrollmentEndDate') }), _jsx(Input, { ref: enrollmentEndDateRef, id: "admin-customer-enrollment-end", type: "date", value: input.enrollmentEndDate, "aria-invalid": Boolean(errors.enrollmentEndDate), "aria-describedby": errors.enrollmentEndDate ? 'admin-customer-enrollment-end-error' : undefined, onChange: (event) => setField('enrollmentEndDate', event.target.value), disabled: busy }), errors.enrollmentEndDate && (_jsx(FieldError, { id: "admin-customer-enrollment-end-error", children: errors.enrollmentEndDate }))] }), _jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-customer-last-contact", children: t('admin.customers.fields.lastContactDate') }), _jsx(Input, { ref: lastContactDateRef, id: "admin-customer-last-contact", type: "date", value: input.lastContactDate, "aria-invalid": Boolean(errors.lastContactDate), "aria-describedby": errors.lastContactDate ? 'admin-customer-last-contact-error' : undefined, onChange: (event) => setField('lastContactDate', event.target.value), disabled: busy }), errors.lastContactDate && (_jsx(FieldError, { id: "admin-customer-last-contact-error", children: errors.lastContactDate }))] })] }), _jsxs(Field, { children: [_jsx(Label, { htmlFor: "admin-customer-details", children: t('admin.customers.fields.details') }), _jsx(Textarea, { ref: detailsRef, id: "admin-customer-details", rows: 7, maxLength: 5000, value: input.details, "aria-invalid": Boolean(errors.details), "aria-describedby": "admin-customer-details-count", onChange: (event) => setField('details', event.target.value), disabled: busy }), _jsx(CharacterCount, { id: "admin-customer-details-count", children: t('admin.customers.form.characterCount', { count: input.details.length, maximum: 5000 }) }), errors.details && _jsx(FieldError, { children: errors.details })] }), _jsxs(DialogActions, { children: [_jsx(SecondaryButton, { type: "button", onClick: onClose, disabled: busy, children: t('admin.customers.actions.cancel') }), _jsx(PrimaryButton, { type: "submit", disabled: busy, children: busy ? t('admin.customers.actions.saving') : t('admin.customers.actions.save') })] })] })] }));
};
const CustomerDetailDialog = ({ customer, locale, canEdit, onEdit, onClose }) => {
    const { t } = useTranslation();
    const detailPlan = customer.planCode === 'custom'
        ? customer.customPlanName || t('admin.customers.planOptions.custom')
        : t(adminCustomerPlanLabelKey(customer.planCode) ?? 'admin.customers.planOptions.none');
    return (_jsxs(Modal, { titleId: "admin-customer-detail-title", onClose: onClose, children: [_jsxs(DialogHeader, { children: [_jsxs("div", { children: [_jsx(Eyebrow, { children: t('admin.customers.detail.eyebrow') }), _jsx(DialogTitle, { id: "admin-customer-detail-title", children: customer.fullName })] }), _jsx(CloseButton, { "data-autofocus": true, type: "button", onClick: onClose, "aria-label": t('admin.customers.actions.close'), children: "\u00D7" })] }), _jsxs(DetailGrid, { children: [_jsx("dt", { children: t('admin.customers.fields.email') }), _jsx("dd", { children: customer.email }), _jsx("dt", { children: t('admin.customers.fields.primaryPhone') }), _jsx("dd", { children: customer.primaryPhone || t('admin.customers.values.notProvided') }), customer.secondaryPhone && (_jsxs(_Fragment, { children: [_jsx("dt", { children: t('admin.customers.fields.secondaryPhone') }), _jsx("dd", { children: customer.secondaryPhone })] })), _jsx("dt", { children: t('admin.customers.fields.salesStatus') }), _jsx("dd", { children: _jsxs(SalesStatusBadge, { "$status": customer.salesStatus, children: [_jsx(StatusMarker, { "aria-hidden": "true", children: salesStatusMarker(customer.salesStatus) }), t(`admin.customers.salesStatus.${customer.salesStatus}`)] }) }), _jsx("dt", { children: t('admin.customers.fields.plan') }), _jsx("dd", { children: detailPlan }), _jsx("dt", { children: t('admin.customers.fields.enrollmentEndDate') }), _jsx("dd", { children: formatCalendarDate(customer.enrollmentEndDate, locale) }), _jsx("dt", { children: t('admin.customers.fields.lastContactDate') }), _jsx("dd", { children: formatCalendarDate(customer.lastContactDate, locale) }), _jsx("dt", { children: t('admin.customers.fields.recordStatus') }), _jsx("dd", { children: t(`admin.customers.recordStatus.${customer.recordStatus}`) }), _jsx("dt", { children: t('admin.customers.fields.createdAt') }), _jsx("dd", { children: formatDate(customer.createdAt, locale) }), _jsx("dt", { children: t('admin.customers.fields.updatedAt') }), _jsx("dd", { children: formatDate(customer.updatedAt, locale) })] }), customer.address && (_jsxs(DetailSection, { children: [_jsx("h3", { children: t('admin.customers.fields.address') }), _jsx(DetailText, { children: customer.address })] })), _jsxs(DetailSection, { children: [_jsx("h3", { children: t('admin.customers.fields.details') }), _jsx(DetailText, { children: customer.details || t('admin.customers.values.noDetails') })] }), _jsxs(DialogActions, { children: [_jsx(SecondaryButton, { type: "button", onClick: onClose, children: t('admin.customers.actions.close') }), canEdit && (_jsx(PrimaryButton, { type: "button", onClick: onEdit, children: t('admin.customers.actions.edit') }))] })] }));
};
const ConfirmationDialog = ({ state, busy, onConfirm, onClose }) => {
    const { t } = useTranslation();
    const archive = state.action === 'archive';
    return (_jsxs(Modal, { titleId: "admin-customer-confirm-title", onClose: busy ? undefined : onClose, children: [_jsx(DialogHeader, { children: _jsxs("div", { children: [_jsx(Eyebrow, { children: t('admin.customers.confirm.eyebrow') }), _jsx(DialogTitle, { id: "admin-customer-confirm-title", children: t(archive ? 'admin.customers.confirm.archiveTitle' : 'admin.customers.confirm.restoreTitle') })] }) }), _jsx(DialogCopy, { children: t(archive ? 'admin.customers.confirm.archiveBody' : 'admin.customers.confirm.restoreBody', {
                    name: state.fullName
                }) }), archive && _jsx(DialogNote, { children: t('admin.customers.confirm.archiveNote') }), _jsxs(DialogActions, { children: [_jsx(SecondaryButton, { "data-autofocus": true, type: "button", onClick: onClose, disabled: busy, children: t('admin.customers.actions.cancel') }), archive ? (_jsx(DangerPrimaryButton, { type: "button", onClick: onConfirm, disabled: busy, children: busy ? t('admin.customers.actions.processing') : t('admin.customers.actions.archive') })) : (_jsx(PrimaryButton, { type: "button", onClick: onConfirm, disabled: busy, children: busy ? t('admin.customers.actions.processing') : t('admin.customers.actions.restore') }))] })] }));
};
const Modal = ({ titleId, onClose, children }) => {
    const dialogRef = useRef(null);
    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialogRef.current?.querySelector('[data-autofocus]')?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, []);
    const keyDown = (event) => {
        if (event.key === 'Escape' && onClose) {
            event.preventDefault();
            onClose();
            return;
        }
        if (event.key !== 'Tab' || !dialogRef.current)
            return;
        const focusable = Array.from(dialogRef.current.querySelectorAll('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex="0"]'));
        if (focusable.length === 0)
            return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };
    return (_jsx(ModalBackdrop, { children: _jsx(ModalCard, { ref: dialogRef, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, onKeyDown: keyDown, children: children }) }));
};
const formatDate = (value, locale) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? '—'
        : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};
const validCalendarInput = (value) => {
    if (!value)
        return true;
    const match = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(value);
    if (!match)
        return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    return (date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day);
};
const formatCalendarDate = (value, locale) => {
    if (!value || !validCalendarInput(value))
        return '—';
    const [year, month, day] = value.split('-').map(Number);
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, day)));
};
const normalizedPhoneInput = (value) => {
    if (!value)
        return '';
    if (value.length > 40 || !/^[+0-9(). -]+$/.test(value))
        return null;
    const compact = value.trim().replace(/[ ().-]+/g, '').replace(/^00/, '+');
    return /^\+?[0-9]{7,15}$/.test(compact) ? compact : null;
};
const salesStatusMarker = (status) => ({
    sold: '✓',
    not_sold: '×',
    new: '●',
    no_response: '…',
    connection_refused: '⊘',
    call_back: '↩'
})[status];
const Panel = styled.section `
  width: min(100%, 92rem);
  display: grid;
  gap: 1.25rem;
`;
const PanelHeader = styled.header `
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.25rem;

  @media (max-width: 40rem) {
    align-items: stretch;
    flex-direction: column;
  }
`;
const Eyebrow = styled.p `
  margin: 0 0 0.45rem;
  color: ${({ theme }) => theme.colors.blue600};
  font-size: 0.75rem;
  font-weight: 750;
  letter-spacing: 0.11em;
  text-transform: uppercase;
`;
const Title = styled.h1 `
  margin: 0;
  font-size: clamp(1.8rem, 4vw, 2.6rem);
  line-height: 1.08;
`;
const Count = styled.p `
  margin: 0.5rem 0 0;
  color: ${({ theme }) => theme.colors.muted};
`;
const Filters = styled.section `
  padding: 1rem;
  display: grid;
  grid-template-columns: minmax(16rem, 1.6fr) repeat(4, minmax(9rem, 0.7fr));
  gap: 0.8rem;
  align-items: end;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 0.85rem;
  background: white;

  @media (max-width: 64rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 36rem) {
    grid-template-columns: 1fr;
  }
`;
const SearchForm = styled.form `
  display: grid;
  gap: 0.4rem;
`;
const SearchRow = styled.div `
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.5rem;
`;
const FilterField = styled.div `
  display: grid;
  gap: 0.4rem;
`;
const Label = styled.label `
  font-size: 0.86rem;
  font-weight: 700;
`;
const controlStyles = `
  width: 100%;
  min-height: 2.75rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid;
  border-radius: 0.55rem;
  background: white;
  font: inherit;
`;
const Input = styled.input `
  ${controlStyles}
  border-color: ${({ theme }) => theme.colors.line};
  color: ${({ theme }) => theme.colors.ink};

  &:focus-visible {
    outline: 3px solid rgba(65, 105, 225, 0.22);
    border-color: ${({ theme }) => theme.colors.blue600};
  }
`;
const Select = styled.select `
  ${controlStyles}
  border-color: ${({ theme }) => theme.colors.line};
  color: ${({ theme }) => theme.colors.ink};
`;
const Textarea = styled.textarea `
  ${controlStyles}
  border-color: ${({ theme }) => theme.colors.line};
  color: ${({ theme }) => theme.colors.ink};
  resize: vertical;
`;
const PrimaryButton = styled.button `
  min-height: 2.75rem;
  padding: 0.65rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.blue600};
  border-radius: 0.6rem;
  color: white;
  background: ${({ theme }) => theme.colors.blue600};
  font: inherit;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 3px solid rgba(65, 105, 225, 0.3);
    outline-offset: 2px;
  }
`;
const SecondaryButton = styled(PrimaryButton) `
  color: ${({ theme }) => theme.colors.ink};
  background: white;
  border-color: ${({ theme }) => theme.colors.line};
`;
const DesktopTableRegion = styled.div `
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 0.85rem;
  background: white;

  &:focus-visible {
    outline: 3px solid rgba(65, 105, 225, 0.22);
  }

  @media (max-width: 48rem) {
    display: none;
  }
`;
const CustomerTable = styled.table `
  width: 100%;
  min-width: 82rem;
  border-collapse: collapse;

  caption {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  th,
  td {
    padding: 0.85rem 0.75rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.line};
    text-align: left;
    vertical-align: top;
    font-size: 0.84rem;
  }

  th {
    color: ${({ theme }) => theme.colors.muted};
    background: ${({ theme }) => theme.colors.surface};
    font-weight: 750;
    white-space: nowrap;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;
const NameCell = styled.td `
  font-weight: 720;
`;
const RecordNote = styled.span `
  display: block;
  margin-top: 0.25rem;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.7rem;
  font-weight: 650;
`;
const SecondaryPhoneIndicator = styled.span `
  display: inline-flex;
  margin-inline-start: 0.4rem;
  padding: 0.08rem 0.32rem;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.muted};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 0.67rem;
  font-weight: 750;
`;
const DetailsPreview = styled.span `
  max-width: 17rem;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: ${({ theme }) => theme.colors.muted};
  line-height: 1.4;
`;
const SalesStatusBadge = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  color: ${({ $status }) => $status === 'sold'
    ? '#185d43'
    : $status === 'not_sold'
        ? '#751c1c'
        : $status === 'new'
            ? '#244a96'
            : $status === 'no_response'
                ? '#6a4b13'
                : '#5b287a'};
  background: ${({ $status }) => $status === 'sold'
    ? '#e7f6ef'
    : $status === 'not_sold'
        ? '#fff1f1'
        : $status === 'new'
            ? '#edf3ff'
            : $status === 'no_response'
                ? '#fff6df'
                : '#f6edfb'};
  font-size: 0.74rem;
  font-weight: 750;
  white-space: nowrap;
`;
const StatusMarker = styled.span `
  font-size: 0.85em;
  line-height: 1;
`;
const RowActions = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;
const ActionButton = styled.button `
  min-height: 2rem;
  padding: 0.3rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 0.45rem;
  color: ${({ theme }) => theme.colors.blue600};
  background: white;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: wait;
    opacity: 0.55;
  }
`;
const DangerButton = styled(ActionButton) `
  color: ${({ theme }) => theme.colors.danger};
`;
const MobileList = styled.div `
  display: none;

  @media (max-width: 48rem) {
    display: grid;
    gap: 0.75rem;
  }
`;
const CustomerCard = styled.article `
  padding: 1rem;
  display: grid;
  gap: 0.85rem;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 0.8rem;
  background: white;
`;
const CardTop = styled.div `
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;
const CardName = styled.h2 `
  margin: 0;
  font-size: 1rem;
`;
const CardEmail = styled.p `
  margin: 0.25rem 0 0;
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.84rem;
  overflow-wrap: anywhere;
`;
const CardGrid = styled.dl `
  margin: 0;
  display: grid;
  grid-template-columns: minmax(6rem, 0.45fr) 1fr;
  gap: 0.45rem 0.7rem;
  font-size: 0.84rem;

  dt {
    color: ${({ theme }) => theme.colors.muted};
  }

  dd {
    margin: 0;
    min-width: 0;
  }
`;
const Pagination = styled.nav `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  span {
    min-width: 8rem;
    text-align: center;
    color: ${({ theme }) => theme.colors.muted};
    font-size: 0.88rem;
  }

  @media (max-width: 28rem) {
    display: grid;
    grid-template-columns: 1fr 1fr;

    span {
      grid-column: 1 / -1;
      grid-row: 1;
    }
  }
`;
const StateBox = styled.div `
  min-height: 12rem;
  padding: 2rem;
  display: grid;
  place-content: center;
  gap: 0.4rem;
  text-align: center;
  border: 1px dashed ${({ theme }) => theme.colors.line};
  border-radius: 0.85rem;
  color: ${({ theme }) => theme.colors.muted};
  background: white;
`;
const ErrorMessage = styled.div `
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  border-radius: 0.45rem;
  color: #751c1c;
  background: #fff1f1;

  @media (max-width: 32rem) {
    align-items: flex-start;
    flex-direction: column;
  }
`;
const SuccessMessage = styled.div `
  padding: 0.8rem 1rem;
  border-left: 3px solid #23865f;
  border-radius: 0.45rem;
  color: #185d43;
  background: #ebf8f2;
`;
const InlineButton = styled.button `
  padding: 0;
  border: 0;
  color: inherit;
  background: transparent;
  font: inherit;
  font-weight: 750;
  text-decoration: underline;
  cursor: pointer;
`;
const ModalBackdrop = styled.div `
  position: fixed;
  z-index: 1000;
  inset: 0;
  padding: clamp(0.75rem, 4vw, 2rem);
  display: grid;
  place-items: center;
  overflow-y: auto;
  background: rgba(23, 26, 46, 0.58);
`;
const ModalCard = styled.div `
  width: min(100%, 44rem);
  max-height: calc(100vh - 2rem);
  padding: clamp(1.1rem, 4vw, 1.75rem);
  overflow-y: auto;
  border-radius: 1rem;
  background: white;
  box-shadow: 0 1.5rem 4rem rgba(19, 36, 66, 0.25);
`;
const DialogHeader = styled.header `
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;
const DialogTitle = styled.h2 `
  margin: 0;
  font-size: clamp(1.45rem, 5vw, 2rem);
`;
const CloseButton = styled.button `
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ink};
  background: white;
  font: inherit;
  font-size: 1.35rem;
  cursor: pointer;
`;
const EditorForm = styled.form `
  margin-top: 1.25rem;
  display: grid;
  gap: 1rem;
`;
const Field = styled.div `
  display: grid;
  gap: 0.4rem;
`;
const TwoColumns = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem;

  @media (max-width: 36rem) {
    grid-template-columns: 1fr;
  }
`;
const FieldError = styled.span `
  color: ${({ theme }) => theme.colors.danger};
  font-size: 0.8rem;
`;
const CharacterCount = styled.span `
  color: ${({ theme }) => theme.colors.muted};
  font-size: 0.78rem;
  text-align: right;
`;
const DialogActions = styled.footer `
  margin-top: 1.25rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;

  @media (max-width: 28rem) {
    display: grid;
    grid-template-columns: 1fr;
  }
`;
const DangerPrimaryButton = styled(PrimaryButton) `
  border-color: ${({ theme }) => theme.colors.danger};
  background: ${({ theme }) => theme.colors.danger};
`;
const DetailGrid = styled.dl `
  margin: 1.25rem 0;
  display: grid;
  grid-template-columns: minmax(8rem, 0.45fr) 1fr;
  gap: 0.6rem 1rem;

  dt {
    color: ${({ theme }) => theme.colors.muted};
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }

  @media (max-width: 32rem) {
    grid-template-columns: 1fr;
    gap: 0.2rem;

    dd + dt {
      margin-top: 0.65rem;
    }
  }
`;
const DetailSection = styled.section `
  padding: 1rem;
  border-radius: 0.75rem;
  background: ${({ theme }) => theme.colors.surface};

  & + & {
    margin-top: 0.75rem;
  }

  h3 {
    margin: 0 0 0.65rem;
    font-size: 0.92rem;
  }
`;
const DetailText = styled.p `
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: 1.6;
`;
const DialogCopy = styled.p `
  margin: 1.25rem 0 0;
  line-height: 1.6;
`;
const DialogNote = styled.p `
  margin: 0.75rem 0 0;
  padding: 0.8rem;
  border-radius: 0.55rem;
  color: ${({ theme }) => theme.colors.muted};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 0.88rem;
  line-height: 1.5;
`;
